'use client';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import clsx from 'clsx';
import React, { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TextInput } from '@/components/ui/text-input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { IMaskInput } from 'react-imask';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useCanAutoFocus } from '@/hooks/useCanAutoFocus';

type CountryConverted = {
  title: string;
  code: string;
  prefix: string;
  masks: { mask: string; lazy: false }[];
  id: string;
  flagEmoji: string;
};

type Props = {
  dialogContentRef?: RefObject<HTMLDivElement | null>;
  countries: CountryConverted[];
  countryId?: string;
  phoneNumber?: string;
  onChange: (newCountryId: string, newPhoneNumber: string, newPrefix?: string) => void;
  ltrField?: boolean;
  autoFocus?: boolean;
};

export const PhoneNumberRegistrationInput: React.FC<Props> = ({
  countries,
  countryId,
  phoneNumber,
  onChange,
  dialogContentRef,
  ltrField = false,
  autoFocus = false,
}) => {
  const [search, setSearch] = useState('');
  const t = useTranslations('registration');

  const triggerRef = useRef<HTMLButtonElement>(null); // <== step 1
  const phoneInputRef = useRef<HTMLInputElement>(null);

  const canAutoFocus = useCanAutoFocus();

  useEffect(() => {
    if (!autoFocus || !canAutoFocus || !countryId) {
      return;
    }
    const frame = requestAnimationFrame(() => phoneInputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [autoFocus, canAutoFocus, countryId]);

  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    // allowed: reading ref in an effect
    setPortalContainer(dialogContentRef?.current || null);
  }, [dialogContentRef]);

  useEffect(() => {
    setIsComplete(false);
  }, [countryId]);

  const selectedCountry = useMemo(() => {
    return countries.find(({ id }) => id === countryId);
  }, [countryId, countries]);

  const filtered = countries.filter(
    (c) => c.title.toLowerCase().includes(search.toLowerCase()) || c.prefix.includes(search),
  );

  const updateCountryId = useCallback(
    (newCountryId: string, newPrefix: string) => {
      onChange(newCountryId, '', newPrefix);
      if (newCountryId) {
        phoneInputRef.current?.focus();
        setTimeout(() => {
          phoneInputRef.current?.focus();
        }, 50);
      }
    },
    [onChange],
  );

  const updatePhoneNumber = useCallback(
    (newPhoneNumber: string) => {
      onChange(countryId || '', newPhoneNumber, selectedCountry?.prefix);
    },
    [onChange, countryId, selectedCountry],
  );

  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);

    if (open === true) {
      setTimeout(() => {
        triggerRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 300);
    }
  };

  return (
    <div className='w-full'>
      <div dir={ltrField ? 'ltr' : undefined} className='flex gap-2'>
        <Popover open={isOpen} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              type='button'
              variant='outline'
              ref={triggerRef}
              className={clsx(
                'w-[128px] min-w-[128px] h-[50px] border-text-disabled-day bg-transparent text-text-primary justify-between gap-[8px] px-2',
                {
                  'border-main-green': isOpen,
                  'justify-end!': !selectedCountry,
                },
              )}
              aria-label='Select country'
            >
              {selectedCountry && (
                <>
                  <Image src={selectedCountry.flagEmoji} width={32} height={24} alt='' />
                  <span className='text-m'>{selectedCountry?.prefix}</span>
                </>
              )}
              {isOpen ? (
                <IconChevronUp className='justify-self-end' size={24} />
              ) : (
                <IconChevronDown className='justify-self-end' size={24} />
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            container={portalContainer}
            avoidCollisions={false}
            align='start'
            side='bottom'
            sideOffset={8}
            id='phone-popover'
            className='w-[327px] p-2'
          >
            <div className='sticky top-0 bg-white z-10 pb-2'>
              <TextInput value={search} onChange={(e) => setSearch(e.target.value)} label={t('country_or_code')} />
            </div>
            <ScrollArea className='[&>[data-slot=scroll-area-viewport]]:max-h-[300px] [&>[data-slot=scroll-area-viewport]>div]:block!'>
              {filtered.map((country) => (
                <Button
                  key={country.code}
                  type='button'
                  variant='ghost'
                  onClick={() => {
                    updateCountryId(country.id, country.prefix);
                    setIsOpen(false);
                  }}
                  className='w-full h-auto justify-between px-3 py-2'
                >
                  <span className='flex items-center gap-2'>
                    <Image src={country.flagEmoji} width={32} height={24} alt='' />
                    <span>{country.title}</span>
                  </span>
                  <span className='text-sm text-gray-500'>{country.prefix}</span>
                </Button>
              ))}
            </ScrollArea>
          </PopoverContent>
        </Popover>

        <IMaskInput
          key={countryId ?? 'no-country'}
          inputRef={phoneInputRef}
          inputMode='numeric'
          mask={(selectedCountry?.masks ?? [{ mask: '', lazy: false }]) as any}
          onAccept={(value: string) => {
            const digits = value.replace(/\D/g, '');
            setIsComplete(false);
            if (digits.length === 0) {
              updatePhoneNumber('');
              return;
            }
            updatePhoneNumber(value);
          }}
          onComplete={(value: string) => {
            setIsComplete(true);
            updatePhoneNumber(value.replace(/-/g, ''));
          }}
          disabled={!countryId}
          className={clsx(
            'min-w-[191px] w-full h-[50px] border disabled:bg-primary-stroke-day bg-transparent rounded-[8px] px-[8px] outline-hidden transition-colors',
            {
              'border-primary-green-day': isComplete,
              'border-text-disabled-day': !isComplete,
            },
          )}
        />
      </div>
    </div>
  );
};
